/**
 * Shared primitives.
 *
 * The rule these follow comes from ARCHITECTURE.md's design system: the panel
 * states things rather than decorating them. A badge carries a status, a tile
 * carries a number, a shadow appears only on hover. Nothing here has a colour or
 * an icon that does not mean something.
 */

import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import { actionOf, messageOf } from "../lib/api/errors";

/* --- buttons -------------------------------------------------------------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "accent" | "ghost" | "danger" | "quiet";
  size?: "md" | "sm";
};

export function Button({
  variant = "ghost",
  size = "md",
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  const classes = ["btn", `btn--${variant}`, size === "sm" ? "btn--sm" : "", className]
    .filter(Boolean)
    .join(" ");
  return <button type={type} className={classes} {...rest} />;
}

/* --- badges --------------------------------------------------------------- */

export type BadgeTone = "pending" | "active" | "danger" | "live" | "muted" | "plain";

export function Badge({ tone = "muted", children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * Status → tone, in one place.
 *
 * The mapping is per-vocabulary because the same word means different things in
 * different tables - a `pending` member is someone to approve, a `pending`
 * listing is a submission to review, and both are the waiting colour. Green is
 * reserved for "live", which is the only thing the design system allows it for.
 */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case "active":
    case "approved":
    case "published":
      return "active";
    case "pending":
    case "disable_requested":
    case "enable_requested":
      return "pending";
    case "suspended":
    case "rejected":
    case "archived":
      return "danger";
    case "deleted":
    case "disabled":
    case "draft":
    case "withdrawn":
    case "unpublished":
      return "muted";
    default:
      return "muted";
  }
}

/** "Live in the directory" - the one place the green dot is used. */
export function LiveBadge({ label = "Listed" }: { label?: string }) {
  return (
    <span className="badge badge--live">
      <span className="dot" />
      {label}
    </span>
  );
}

/* --- cards ---------------------------------------------------------------- */

export function Card({
  children,
  flush = false,
  className = "",
}: {
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return <section className={["card", flush ? "card--flush" : "", className].filter(Boolean).join(" ")}>{children}</section>;
}

export function CardHeader({
  title,
  hint,
  actions,
}: {
  title: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="card__header">
      <div>
        <h2 className="card__title">{title}</h2>
        {hint ? <p className="card__hint">{hint}</p> : null}
      </div>
      {actions ? <div className="row row--tight">{actions}</div> : null}
    </header>
  );
}

/* --- forms ---------------------------------------------------------------- */

type FieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
};

/**
 * A labelled control. The label is uppercase-wide-tracked per the design system,
 * and the control is handed its id so the label's `htmlFor` always matches.
 */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error">{error}</span> : null}
    </div>
  );
}

/**
 * Inputs, selects and textareas are thin wrappers so every control in the app
 * gets the same class and the same id wiring without a per-call-site className.
 */
export function Input({ className = "", ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input ${className}`.trim()} {...rest} />;
}

export function Select({ className = "", children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`select ${className}`.trim()} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ className = "", ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`textarea ${className}`.trim()} {...rest} />;
}

/* --- states --------------------------------------------------------------- */

/**
 * Empty states always carry a next action in plain language (NFR-10.5.4).
 *
 * The copy is a parameter rather than a default because the server writes its own
 * for the list endpoints (`message` / `action`), and the convention across this
 * repo is to render the server's sentence rather than a second copy that can
 * drift from it.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string | null;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h3 className="empty__title">{title}</h3>
      {body ? <p className="empty__body">{body}</p> : null}
      {action ? <div className="row row--tight">{action}</div> : null}
    </div>
  );
}

/**
 * An error, rendered from whatever the API sent.
 *
 * `ApiError` carries a human `message` and an `action` from `app/errors.py`, so
 * the honest thing is to show both verbatim. A `NetworkError` (the API is down,
 * or the request timed out) has no server sentence and says so itself.
 */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const message = messageOf(error);
  const action = actionOf(error);
  return (
    <div className="alert" role="alert">
      <span className="alert__title">{message}</span>
      {action ? <span className="alert__action">{action}</span> : null}
      {onRetry ? (
        <div className="row row--tight" style={{ marginTop: 6 }}>
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** A confirmation or clarification shown after a mutation. The sentence is the
 *  server's `message`, so a screen never claims more than the API did. */
export function SuccessNotice({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div className="alert alert--ok" role="status">
      <span className="alert__title">{message}</span>
      {onDismiss ? (
        <div className="row row--tight" style={{ marginTop: 6 }}>
          <Button size="sm" variant="quiet" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function LoadingRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }, (_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }, (_, columnIndex) => (
                <td key={columnIndex}>
                  <div className="skeleton" style={{ width: columnIndex === 0 ? "60%" : "38%" }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --- pagination ---------------------------------------------------------- */

/**
 * Offset paging, driven entirely by the server's `limit` / `offset` / `total`.
 *
 * The component never computes a page count itself: `total` is the server's, and
 * re-deriving `Math.ceil(total / limit)` here would be a second implementation of
 * something the response already states.
 */
export function Pagination({
  total,
  limit,
  offset,
  onChange,
  noun = "rows",
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
  noun?: string;
}) {
  if (total === 0) return null;
  const first = offset + 1;
  const last = Math.min(offset + limit, total);
  const hasPrevious = offset > 0;
  const hasNext = last < total;

  return (
    <div className="pager">
      <span className="pager__status">
        {first}–{last} of {total} {noun}
      </span>
      <div className="row row--tight">
        <Button
          size="sm"
          disabled={!hasPrevious}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button size="sm" disabled={!hasNext} onClick={() => onChange(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}

/* --- tabs ----------------------------------------------------------------- */

export function Tabs<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          role="tab"
          aria-selected={option.value === value}
          className={`tab ${option.value === value ? "tab--active" : ""}`}
          onClick={() => onChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* --- definition lists ---------------------------------------------------- */

export function DefinitionList({ children }: { children: ReactNode }) {
  return <dl className="dl">{children}</dl>;
}

export function Definition({
  term,
  children,
}: {
  term: string;
  children: ReactNode;
}) {
  const isEmpty =
    children === null ||
    children === undefined ||
    (typeof children === "string" && children.trim() === "");
  return (
    <>
      <dt>{term}</dt>
      <dd className={isEmpty ? "is-empty" : undefined}>{isEmpty ? "-" : children}</dd>
    </>
  );
}

/* --- avatar -------------------------------------------------------------- */

/**
 * A member's photo, or their initials.
 *
 * The fallback is initials rather than a generic silhouette: the panel is used to
 * find a person among strangers with similar names, and two letters distinguish
 * far better than one identical icon.
 */
export function Avatar({ name, url }: { name: string; url?: string | null }) {
  if (url) return <img className="thumb" src={url} alt="" loading="lazy" />;
  const letters = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  return <span className="thumb thumb--placeholder">{letters || "?"}</span>;
}

/* --- modal --------------------------------------------------------------- */

/**
 * A dialog, used for the decisions that need a reason typed in.
 *
 * Closing on `Escape` and on a backdrop click are both supported, but the
 * destructive actions inside always go through a confirm of their own - a
 * backdrop click must never be able to submit anything.
 */
export function Modal({
  title,
  description,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    // Focus the first control so a keyboard user is already inside the dialog;
    // without this the tab order starts at the top of the page behind it.
    const first = panel.current?.querySelector<HTMLElement>(
      "input, select, textarea, button",
    );
    first?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`modal ${wide ? "modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panel}
      >
        <h2 className="modal__title">{title}</h2>
        {description ? <div className="modal__body">{description}</div> : null}
        {children}
      </div>
    </div>
  );
}

/* --- layout -------------------------------------------------------------- */

/**
 * A screen's outer wrapper: the column, the gap, and the max width.
 *
 * Lives here rather than beside the shell because it is the frame every PAGE
 * draws, and a page that had to import the shell would pull in the nav and the
 * session for a container.
 */
export function Page({ children }: { children: ReactNode }) {
  return <div className="stack">{children}</div>;
}

/* --- misc ---------------------------------------------------------------- */

/** A labelled figure. Used on the dashboard tiles and the report summaries. */
export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div>
      <span className="label">{label}</span>
      <p className="figure" style={{ fontSize: 26, margin: "4px 0 0" }}>
        {value}
      </p>
      {hint ? <p className="text-small text-meta" style={{ margin: 0 }}>{hint}</p> : null}
    </div>
  );
}

/** Pretty-printed JSON for the audit log's state snapshots. */
export function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-meta text-small">-</span>;
  }
  return <pre className="json">{JSON.stringify(value, null, 2)}</pre>;
}
